import React, { useCallback, useEffect, useState } from "react";
import { Button, Table, Tag, Select, Dropdown, Badge, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import PromotionFormModal from "./PromotionFormModal";
import CancelPromotionModal from "./CancelPromotionModal";
import { formatDateTime } from "../../utils/helper";
import api from "../../api";
import {
  PRODUCT_TYPE_COLOR,
  PRODUCT_TYPE_LABEL,
  PRODUCT_TYPE_OPTIONS,
} from "../../constants";

const getStatus = (record) => {
  const now = new Date();
  const ended = new Date(record.endsAt) < now;
  if (!record.isActive) return "stopped";
  if (ended) return "ended";
  return "active";
};

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [formModal, setFormModal] = useState({ open: false });
  const [editingRow, setEditingRow] = useState(null);
  const [cancelModal, setCancelModal] = useState({ open: false, id: null });
  const [cancelling, setCancelling] = useState(false);

  // Filters
  const [productType, setProductType] = useState(undefined);
  const [isActive, setIsActive] = useState(undefined);
  const [page, setPage] = useState(1);

  const toast = useToast();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await api.promotions_list({
      page,
      limit: 10,
      ...(productType && { productType }),
      ...(isActive !== undefined && { isActive }),
    });
    // console.log("promotion --->", res);
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page, productType, isActive]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditingRow(null);
    setFormModal({ open: true });
  };

  const openEdit = (record) => {
    setEditingRow(record);
    setFormModal({ open: true });
  };

  const onFormSuccess = (data, isEdit) => {
    toast(isEdit ? "Амжилттай засагдлаа" : "Амжилттай нэмэгдлээ", "success");
    fetchRows();
  };

  const onCancel = async (reason) => {
    setCancelling(true);
    const res = await api.promotions_cancel(cancelModal.id, {
      cancelReason: reason,
    });
    if (res?.status === 200) {
      toast("Promotion зогсоогдлоо", "info");
      fetchRows();
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
    setCancelling(false);
    setCancelModal({ open: false, id: null });
  };

  const columns = [
    {
      title: "Төрөл",
      dataIndex: "productType",
      width: 80,
      render: (v) => (
        <Tag color={PRODUCT_TYPE_COLOR(v)}>{PRODUCT_TYPE_LABEL(v) || v}</Tag>
      ),
    },
    {
      title: "Бүтээгдэхүүн",
      key: "product",
      render: (_, r) => (
        <div>
          <div className="fw-semibold" style={{ fontSize: 14 }}>
            {r.product?.name || r.productId}
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            {r.product?.issuer?.name}
          </div>
        </div>
      ),
    },
    {
      title: "Хугацаа",
      key: "duration",
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div>{formatDateTime(r.startsAt)}</div>
          <div style={{ color: "#888" }}>~ {formatDateTime(r.endsAt)}</div>
        </div>
      ),
    },
    {
      title: "Эрэмбэ",
      dataIndex: "priority",
      width: 70,
      render: (v) => v ?? 0,
    },
    {
      title: "Төлөв",
      key: "status",
      width: 150,
      render: (_, r) => {
        const now = new Date();
        const ended = new Date(r.endsAt) < now;
        if (!r.isActive) return <Badge status="error" text="Зогссон" />;
        if (ended) return <Badge status="default" text="Дууссан" />;
        return <Badge status="success" text="Идэвхтэй" />;
      },
    },
    {
      title: "Нэмсэн",
      key: "creator",
      render: (_, r) => (
        <span style={{ fontSize: 12, color: "#666" }}>
          {r.creator?.name || "—"}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const status = getStatus(record);

        if (status === "stopped") {
          return (
            <Tooltip title={`Шалтгаан: ${record.cancelReason || "—"}`}>
              <Tag color={"red"} style={{ fontSize: 12 }}>
                Зогсоосон
              </Tag>
            </Tooltip>
          );
        }

        if (status === "ended") {
          return (
            <span style={{ fontSize: 12, color: "#bbb" }}>Хугацаа дууссан</span>
          );
        }

        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  label: <span style={{ fontSize: 13 }}>Засах</span>,
                },
                {
                  key: "cancel",
                  label: (
                    <span style={{ fontSize: 13, color: "#fa8c16" }}>
                      Зогсоох
                    </span>
                  ),
                },
              ],
              onClick: ({ key }) => {
                if (key === "edit") openEdit(record);
                if (key === "cancel")
                  setCancelModal({ open: true, id: record.id });
              },
            }}
            trigger={["click"]}
          >
            <Button variant="filled" color="default" style={{ fontSize: 12 }}>
              Үйлдэл <IconArrowRight size={12} />
            </Button>
          </Dropdown>
        );
      },
      // render: (_, record) => {
      //   if (!record.isActive) return null;
      //   const menuItems = [];

      //   if (record.isActive) {
      //     menuItems.push(
      //       { key: "edit", label: <span style={{ fontSize: 13 }}>Засах</span> },
      //       {
      //         key: "cancel",
      //         label: (
      //           <span style={{ fontSize: 13, color: "#fa8c16" }}>Зогсоох</span>
      //         ),
      //       },
      //     );
      //   }

      //   return (
      //     <Dropdown
      //       menu={{
      //         items: menuItems,
      //         onClick: ({ key }) => {
      //           if (key === "edit") openEdit(record);
      //           if (key === "cancel")
      //             setCancelModal({ open: true, id: record.id });
      //         },
      //       }}
      //       trigger={["click"]}
      //     >
      //       <Button variant="filled" color="default" style={{ fontSize: 12 }}>
      //         Үйлдэл <IconArrowRight size={12} />
      //       </Button>
      //     </Dropdown>
      //   );
      // },
    },
  ];

  return (
    <section className="promotions-section">
      <PageHeader
        title="Promotion"
        sub="Бүтээгдэхүүний онцлох байршил удирдах"
        rSide={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Нэмэх
          </Button>
        }
      />

      <div className="toolbar">
        <Select
          className="status"
          placeholder="Бүтээгдэхүүний төрөл"
          allowClear
          value={productType}
          onChange={(v) => {
            setProductType(v);
            setPage(1);
          }}
          options={PRODUCT_TYPE_OPTIONS}
        />
        <Select
          className="status"
          placeholder="Төлөв"
          allowClear
          value={isActive}
          onChange={(v) => {
            setIsActive(v);
            setPage(1);
          }}
          options={[
            { value: true, label: "Идэвхтэй" },
            { value: false, label: "Зогссон" },
          ]}
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      {/* Нэмэх / Засах modal */}
      <PromotionFormModal
        open={formModal.open}
        onClose={() => setFormModal({ open: false })}
        editingRow={editingRow}
        onSuccess={onFormSuccess}
      />

      {/* Зогсоох modal */}
      <CancelPromotionModal
        open={cancelModal.open}
        onClose={() => setCancelModal({ open: false, id: null })}
        onConfirm={onCancel}
        loading={cancelling}
      />
    </section>
  );
}
